import { type FormEvent, type JSX, type ReactNode, useId, useRef } from 'react';

import { Abschlussreihe } from './Abschlussreihe';
import { useKopfhandlungen } from './Seitenkontext';

/**
 * DER SEITENTYP ERFASSUNG (ÄA-41, Entwurf v2) — Kunde, Angebot, Auftrag,
 * Rahmenauftrag und Artikel anlegen, Dubletten zusammenführen, Buchung
 * erfassen.
 *
 * Kopf ohne Kennzahlen, darunter EINE Karte mit aufklappbaren Abschnitten
 * (`Abschnitt`) und Feldraster. Die Handlungen stehen zweimal: oben rechts im
 * Seitenkopf — wer ein kurzes Formular füllt, sieht sie ohne zu rollen — und
 * am Ende, wo das Auge nach dem letzten Feld landet (2.3: Abbruch links,
 * Abschluss rechts).
 *
 * Der Knopf im Kopf steht außerhalb des Formulars und sendet es über das
 * Attribut `form` ab. Es bleibt EIN Formular mit EINER Prüfung — der Knopf
 * oben ist kein zweiter Weg mit eigener Logik.
 */
export function Erfassung({
  beiAbsenden,
  absenden,
  abbrechen,
  zusatz,
  beschriftung,
  children,
}: {
  readonly beiAbsenden: () => void;
  readonly absenden: { readonly text: string; readonly gesperrt?: boolean };
  readonly abbrechen?: { readonly text: string; readonly beiKlick: () => void };
  /** Weitere Knöpfe am Ende, zwischen Abbruch und Abschluss. */
  readonly zusatz?: ReactNode;
  /** Der Name des Formulars für Vorlesehilfen. */
  readonly beschriftung: string;
  readonly children: ReactNode;
}): JSX.Element {
  const kennung = `erfassung-${useId().replace(/:/g, '')}`;
  const gesperrt = absenden.gesperrt === true;
  /*
   * Der Rückruf im Ref: Er entsteht beim Aufrufer bei jedem Bild neu, und als
   * Abhängigkeit setzte er den Seitenkopf ohne Ende neu.
   */
  const abbruch = useRef(abbrechen?.beiKlick);
  abbruch.current = abbrechen?.beiKlick;

  useKopfhandlungen(
    <>
      {abbrechen !== undefined && (
        <button type="button" className="knopf" onClick={() => abbruch.current?.()}>
          {abbrechen.text}
        </button>
      )}
      <button type="submit" form={kennung} className="knopf knopf--haupt" disabled={gesperrt}>
        {absenden.text}
      </button>
    </>,
    [kennung, gesperrt, absenden.text, abbrechen?.text],
  );

  return (
    <form
      id={kennung}
      className="karte erfassung__karte"
      aria-label={beschriftung}
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        beiAbsenden();
      }}
    >
      <div className="tafel__abschnitte">{children}</div>
      <Abschlussreihe
        abbruch={
          abbrechen !== undefined ? (
            <button type="button" className="knopf" onClick={abbrechen.beiKlick}>
              {abbrechen.text}
            </button>
          ) : undefined
        }
      >
        {zusatz}
        <button type="submit" className="knopf knopf--haupt" disabled={gesperrt}>
          {absenden.text}
        </button>
      </Abschlussreihe>
    </form>
  );
}
